import { el, toast } from './ui'

const active = new WeakMap<HTMLElement, { timer: number; restore: () => void }>()
export const COPIED_TEXT = 'Copied'
const COPIED_MS = 2000
let notice: HTMLElement | null = null

// One successful-copy receipt. Repeated copies restart the two seconds without losing the original label.
export function showCopied(button: HTMLElement, restore?: () => void, ms = COPIED_MS, label: HTMLElement = button): void {
  const previous = active.get(button)
  if (previous) window.clearTimeout(previous.timer)
  const children = [...label.childNodes]
  const reset = previous?.restore ?? (() => {
    if (restore) restore(); else label.replaceChildren(...children)
    button.classList.remove('is-copied')
  })
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16'); svg.setAttribute('aria-hidden', 'true')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'M3 8.5l3.2 3.2L13 5'); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2.2'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round')
  svg.append(path)
  label.replaceChildren(el('span', { class: 'copied-label', role: 'status', 'aria-live': 'polite' }, svg, COPIED_TEXT))
  button.classList.add('is-copied')
  const timer = window.setTimeout(() => { reset(); active.delete(button) }, ms)
  active.set(button, { timer, restore: reset })
}

// A copy that closes a sheet or changes page still needs its two-second receipt.
export function showCopiedNotice(hint?: string): void {
  dismissNotice()
  document.querySelector('#toast')?.remove()
  // Own the element and timer. A toast implementation need not insert a queryable #toast.
  const receipt = el('div', { id: 'toast', class: 'copied-notice', role: 'status' })
  document.body.append(receipt)
  notice = receipt
  showCopied(receipt, () => { receipt.remove(); if (notice === receipt) notice = null })
  if (hint) receipt.append(el('span', { class: 'copied-help' }, hint))
}

function dismissNotice(): void {
  if (!notice) return
  const previous = active.get(notice)
  if (previous) { window.clearTimeout(previous.timer); active.delete(notice) }
  notice.remove(); notice = null
}

export function showCopyFailed(button: HTMLElement | null, what: string): void {
  if (button) {
    const previous = active.get(button)
    if (previous) { window.clearTimeout(previous.timer); previous.restore(); active.delete(button) }
  }
  dismissNotice()
  toast(`Couldn't copy. Select the ${what} and copy it by hand.`)
}

type CopyOptions = { what: string } & (
  { button: HTMLElement; label?: HTMLElement; restore?: () => void; notice?: never }
  | { notice: true | string; button?: never }
)

// The app's only clipboard writer. Callers can keep selection/retry/navigation behaviour using the result.
export async function copyText(text: string, opts: CopyOptions): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    showCopyFailed(opts.button ?? null, opts.what)
    return false
  }
  if (opts.button) showCopied(opts.button, opts.restore, COPIED_MS, opts.label ?? opts.button)
  else showCopiedNotice(typeof opts.notice === 'string' ? opts.notice : undefined)
  return true
}
